/**
 * Transaction engine error taxonomy.
 *
 * Two error spaces flow through here:
 *
 * 1. Engine codes (`TxErrorCode`) — the tx-engine's own classification of
 *    failures at each stage: simulate / sign / relay / confirm. These are
 *    raised inside the engine and never originate from chain.
 *
 * 2. Program/SPL error names — the on-chain `#[error_code]` enum variants
 *    surfaced by the Anchor runtime, plus the well-known SPL Token error
 *    names. `CONTRACT_ERROR_MESSAGES` is typed `Record<OpinionErrorName, string>`
 *    so codegen-driven `OpinionErrorName` (sourced from
 *    `contracts/markets/programs/markets/src/error/*.rs` via
 *    `scripts/generate-error-package.ts`) is exhaustive at compile time;
 *    adding a contract variant without adding an i18n key fails the build.
 *
 * Every entry in either map points to a flat `tx.errors.<camelCase>` i18n
 * key. The frontend's global mutation funnel resolves the key via
 * `useTranslations()` before rendering.
 */

import {
  CP_SWAP_ERRORS,
  type CpSwapErrorName,
  OPINION_ERRORS,
  type OpinionErrorName,
  SELL_CAP_ERRORS,
  type SellCapErrorName,
} from '@morfi/errors';
import type { RecoveryAction, TxStage } from '../types.js';

export const TxErrorCode = {
  TX_SIM_FAILED: 'TX_SIM_FAILED',
  TX_SIM_TIMEOUT: 'TX_SIM_TIMEOUT',
  TX_PRIORITY_FEE_FAILED: 'TX_PRIORITY_FEE_FAILED',
  TX_BUILD_FAILED: 'TX_BUILD_FAILED',
  TX_SIGN_FAILED: 'TX_SIGN_FAILED',
  TX_SIGN_TIMEOUT: 'TX_SIGN_TIMEOUT',
  TX_SESSION_EXPIRED: 'TX_SESSION_EXPIRED',
  TX_RELAY_INVALID: 'TX_RELAY_INVALID',
  TX_RELAY_SIM_FAILED: 'TX_RELAY_SIM_FAILED',
  TX_RELAY_RATE_LIMITED: 'TX_RELAY_RATE_LIMITED',
  TX_RELAY_FEE_PAYER_LOW: 'TX_RELAY_FEE_PAYER_LOW',
  TX_RELAY_REJECTED: 'TX_RELAY_REJECTED',
  TX_RELAY_TIMEOUT: 'TX_RELAY_TIMEOUT',
  TX_CONFIRM_TIMEOUT: 'TX_CONFIRM_TIMEOUT',
  TX_CONFIRM_FAILED: 'TX_CONFIRM_FAILED',
  TX_BLOCKHASH_EXPIRED: 'TX_BLOCKHASH_EXPIRED',
  TX_MAX_RETRIES: 'TX_MAX_RETRIES',
  TX_UNKNOWN: 'TX_UNKNOWN',
  TX_TURNKEY_FAILED: 'TX_TURNKEY_FAILED',
  TX_TURNKEY_TIMEOUT: 'TX_TURNKEY_TIMEOUT',
  TX_TURNKEY_NOT_CONFIGURED: 'TX_TURNKEY_NOT_CONFIGURED',
  TX_TURNKEY_RATE_LIMITED: 'TX_TURNKEY_RATE_LIMITED',
  TX_TURNKEY_QUOTA_EXCEEDED: 'TX_TURNKEY_QUOTA_EXCEEDED',
  TX_TURNKEY_ENCODE_FAILED: 'TX_TURNKEY_ENCODE_FAILED',
  TX_TOO_LARGE: 'TX_TOO_LARGE',
} as const;

export type TxErrorCodeType = (typeof TxErrorCode)[keyof typeof TxErrorCode];

export type RecoveryActionType = RecoveryAction['type'];

const TX_STAGES: readonly TxStage[] = [
  'preflight',
  'building',
  'signing',
  'relaying',
  'confirming',
  'confirmed',
  'failed',
  'cancelled',
];

const RECOVERY_ACTION_TYPES: readonly RecoveryActionType[] = [
  'not_retryable',
  'prompt_retry',
  'prompt_retry_with_higher_slippage',
  'auto_retry',
  'auto_retry_after_delay',
  'informational',
];

export function isTxErrorCode(value: unknown): value is TxErrorCodeType {
  return (
    typeof value === 'string' && (Object.values(TxErrorCode) as readonly string[]).includes(value)
  );
}

export function safeTxErrorCode(value: unknown): TxErrorCodeType {
  return isTxErrorCode(value) ? value : TxErrorCode.TX_UNKNOWN;
}

export function safeTxStage(value: unknown): TxStage {
  return typeof value === 'string' && (TX_STAGES as readonly string[]).includes(value)
    ? (value as TxStage)
    : 'failed';
}

export function safeRecoveryActionType(value: unknown): RecoveryActionType {
  return typeof value === 'string' && (RECOVERY_ACTION_TYPES as readonly string[]).includes(value)
    ? (value as RecoveryActionType)
    : 'prompt_retry';
}

export const TX_ERROR_MESSAGES: Record<TxErrorCodeType, string> = {
  [TxErrorCode.TX_SIM_FAILED]: 'tx.errors.simFailed',
  [TxErrorCode.TX_SIM_TIMEOUT]: 'tx.errors.simTimeout',
  [TxErrorCode.TX_PRIORITY_FEE_FAILED]: 'tx.errors.priorityFeeFailed',
  [TxErrorCode.TX_BUILD_FAILED]: 'tx.errors.buildFailed',
  [TxErrorCode.TX_SIGN_FAILED]: 'tx.errors.signFailed',
  [TxErrorCode.TX_SIGN_TIMEOUT]: 'tx.errors.signTimeout',
  [TxErrorCode.TX_SESSION_EXPIRED]: 'tx.errors.sessionExpired',
  [TxErrorCode.TX_RELAY_INVALID]: 'tx.errors.relayInvalid',
  [TxErrorCode.TX_RELAY_SIM_FAILED]: 'tx.errors.relaySimFailed',
  [TxErrorCode.TX_RELAY_RATE_LIMITED]: 'tx.errors.relayRateLimited',
  [TxErrorCode.TX_RELAY_FEE_PAYER_LOW]: 'tx.errors.relayFeePayerLow',
  [TxErrorCode.TX_RELAY_REJECTED]: 'tx.errors.relayRejected',
  [TxErrorCode.TX_RELAY_TIMEOUT]: 'tx.errors.relayTimeout',
  [TxErrorCode.TX_CONFIRM_TIMEOUT]: 'tx.errors.confirmTimeout',
  [TxErrorCode.TX_CONFIRM_FAILED]: 'tx.errors.confirmFailed',
  [TxErrorCode.TX_BLOCKHASH_EXPIRED]: 'tx.errors.blockhashExpired',
  [TxErrorCode.TX_MAX_RETRIES]: 'tx.errors.maxRetries',
  [TxErrorCode.TX_UNKNOWN]: 'tx.errors.unknown',
  [TxErrorCode.TX_TURNKEY_FAILED]: 'tx.errors.turnkeyFailed',
  [TxErrorCode.TX_TURNKEY_TIMEOUT]: 'tx.errors.turnkeyTimeout',
  [TxErrorCode.TX_TURNKEY_NOT_CONFIGURED]: 'tx.errors.turnkeyNotConfigured',
  [TxErrorCode.TX_TURNKEY_RATE_LIMITED]: 'tx.errors.turnkeyRateLimited',
  [TxErrorCode.TX_TURNKEY_QUOTA_EXCEEDED]: 'tx.errors.turnkeyQuotaExceeded',
  [TxErrorCode.TX_TURNKEY_ENCODE_FAILED]: 'tx.errors.turnkeyEncodeFailed',
  [TxErrorCode.TX_TOO_LARGE]: 'tx.errors.tooLarge',
};

function camelCase(name: string): string {
  return name.charAt(0).toLowerCase() + name.slice(1);
}

/** Markets error → i18n key, including errors only the sell-cap program variant raises. */
export const CONTRACT_ERROR_MESSAGES: Record<OpinionErrorName | SellCapErrorName, string> =
  Object.fromEntries(
    (
      [...Object.keys(OPINION_ERRORS), ...Object.keys(SELL_CAP_ERRORS)] as (
        | OpinionErrorName
        | SellCapErrorName
      )[]
    ).map((name) => [name, `tx.errors.${camelCase(name)}`]),
  ) as Record<OpinionErrorName | SellCapErrorName, string>;

/**
 * cp-swap (graduated-market AMM) error → i18n key. Kept separate from
 * {@link CONTRACT_ERROR_MESSAGES} because cp-swap is a distinct program whose
 * codes overlap markets'; the contract-sdk parser attributes by program id.
 * Exhaustive over `CpSwapErrorName` at compile time — adding a cp-swap variant
 * without an i18n key fails the build.
 */
export const CP_SWAP_ERROR_MESSAGES: Record<CpSwapErrorName, string> = Object.fromEntries(
  (Object.keys(CP_SWAP_ERRORS) as CpSwapErrorName[]).map((name) => [
    name,
    `tx.errors.${camelCase(name)}`,
  ]),
) as Record<CpSwapErrorName, string>;

export const SPL_TOKEN_ERROR_NAMES = [
  'NotRentExempt',
  'InsufficientFunds',
  'InvalidMint',
  'MintMismatch',
  'OwnerMismatch',
  'FixedSupply',
  'AlreadyInUse',
  'InvalidNumberOfProvidedSigners',
  'InvalidNumberOfRequiredSigners',
  'UninitializedState',
  'NativeNotSupported',
  'NonNativeHasBalance',
  'InvalidInstruction',
  'InvalidState',
  'Overflow',
  'AuthorityTypeNotSupported',
  'MintCannotFreeze',
  'AccountFrozen',
] as const;

export type SplTokenErrorName = (typeof SPL_TOKEN_ERROR_NAMES)[number];

export const SPL_TOKEN_ERROR_MESSAGES: Record<SplTokenErrorName, string> = Object.fromEntries(
  SPL_TOKEN_ERROR_NAMES.map((name) => [name, `tx.errors.${camelCase(name)}`]),
) as Record<SplTokenErrorName, string>;

export function lookupErrorMessageKey(errorName: string): string | undefined {
  if (errorName in CONTRACT_ERROR_MESSAGES) {
    return CONTRACT_ERROR_MESSAGES[errorName as OpinionErrorName | SellCapErrorName];
  }
  if (errorName in CP_SWAP_ERROR_MESSAGES) {
    return CP_SWAP_ERROR_MESSAGES[errorName as CpSwapErrorName];
  }
  if ((SPL_TOKEN_ERROR_NAMES as readonly string[]).includes(errorName)) {
    return SPL_TOKEN_ERROR_MESSAGES[errorName as SplTokenErrorName];
  }
  return undefined;
}

export class TxError extends Error {
  readonly code: TxErrorCodeType;
  readonly stage: TxStage;
  readonly userMessageKey: string;
  readonly traceId: string;
  readonly recoveryAction: RecoveryAction;
  readonly details: Record<string, unknown> | undefined;
  readonly morfiError: string | undefined;

  constructor(params: {
    code: TxErrorCodeType;
    stage: TxStage;
    traceId: string;
    recoveryAction: RecoveryAction;
    userMessageKey?: string | undefined;
    details?: Record<string, unknown> | undefined;
    morfiError?: string | undefined;
  }) {
    super(TX_ERROR_MESSAGES[params.code]);
    this.name = 'TxError';
    this.code = params.code;
    this.stage = params.stage;
    this.traceId = params.traceId;
    this.recoveryAction = params.recoveryAction;
    this.userMessageKey = params.userMessageKey ?? TX_ERROR_MESSAGES[params.code];
    this.details = params.details;
    this.morfiError = params.morfiError;
  }
}

const NON_RETRYABLE_SPL_ERRORS: ReadonlySet<string> = new Set<SplTokenErrorName>([
  'InsufficientFunds',
  'AccountFrozen',
  'OwnerMismatch',
  'InvalidMint',
  'MintMismatch',
]);

export function getRecoveryAction(
  code: TxErrorCodeType,
  morfiError?: string,
  currentSlippageBps?: number,
): RecoveryAction {
  // Slippage failures retry with a higher tolerance. The markets bonding curve
  // raises `SlippageExceeded`; the cp-swap AMM (graduated markets) raises
  // `ExceededSlippage` — both mean the same thing to the user.
  if (morfiError === 'SlippageExceeded' || morfiError === 'ExceededSlippage') {
    const current = currentSlippageBps ?? 100;
    const suggested = Math.min(Math.ceil(current * 1.5), 1000);
    return { type: 'prompt_retry_with_higher_slippage', suggestedSlippageBps: suggested };
  }

  if (
    morfiError !== undefined &&
    (morfiError in CONTRACT_ERROR_MESSAGES || morfiError in CP_SWAP_ERROR_MESSAGES)
  ) {
    // Every other on-chain contract error (markets or cp-swap) is a
    // state/permission/validation failure that the same tx will keep hitting
    // until the caller changes their inputs. Don't auto-retry.
    return { type: 'not_retryable' };
  }

  if (morfiError !== undefined && NON_RETRYABLE_SPL_ERRORS.has(morfiError)) {
    return { type: 'not_retryable' };
  }

  switch (code) {
    case TxErrorCode.TX_SIM_FAILED:
    case TxErrorCode.TX_RELAY_SIM_FAILED:
      return { type: 'prompt_retry' };

    case TxErrorCode.TX_RELAY_RATE_LIMITED:
      return { type: 'auto_retry' };

    case TxErrorCode.TX_SIGN_FAILED:
    case TxErrorCode.TX_SIGN_TIMEOUT:
      return { type: 'prompt_retry' };

    // The Turnkey signing session is dead (expired or scoped to a different
    // sub-org than the active wallet). No retry can succeed — only a fresh
    // login re-mints the session; the submit seam auto-triggers it.
    case TxErrorCode.TX_SESSION_EXPIRED:
      return { type: 'not_retryable' };

    case TxErrorCode.TX_RELAY_INVALID:
    case TxErrorCode.TX_RELAY_FEE_PAYER_LOW:
      return { type: 'not_retryable' };

    case TxErrorCode.TX_RELAY_REJECTED:
    case TxErrorCode.TX_RELAY_TIMEOUT:
      return { type: 'auto_retry' };

    case TxErrorCode.TX_BLOCKHASH_EXPIRED:
      return { type: 'auto_retry' };

    case TxErrorCode.TX_CONFIRM_TIMEOUT:
      return { type: 'informational' };

    case TxErrorCode.TX_CONFIRM_FAILED:
      return { type: 'prompt_retry' };

    case TxErrorCode.TX_MAX_RETRIES:
      return { type: 'not_retryable' };

    case TxErrorCode.TX_SIM_TIMEOUT:
    case TxErrorCode.TX_PRIORITY_FEE_FAILED:
      return { type: 'informational' };

    case TxErrorCode.TX_TURNKEY_FAILED:
      return { type: 'not_retryable' };

    case TxErrorCode.TX_TURNKEY_TIMEOUT:
      return { type: 'prompt_retry' };

    case TxErrorCode.TX_BUILD_FAILED:
    case TxErrorCode.TX_TURNKEY_ENCODE_FAILED:
      return { type: 'not_retryable' };

    // The same tx will never fit. The swap mutation intercepts this code and
    // falls back to two separate transactions; for any other caller it is a
    // definitive, non-retryable failure.
    case TxErrorCode.TX_TOO_LARGE:
      return { type: 'not_retryable' };

    case TxErrorCode.TX_TURNKEY_NOT_CONFIGURED:
    case TxErrorCode.TX_TURNKEY_RATE_LIMITED:
    case TxErrorCode.TX_TURNKEY_QUOTA_EXCEEDED:
      return { type: 'informational' };

    default:
      return { type: 'prompt_retry' };
  }
}

export function createTxError(
  code: TxErrorCodeType,
  traceId: string,
  stage: TxStage,
  details?: Record<string, unknown>,
): TxError {
  const morfiError = details?.morfiError as string | undefined;
  const currentSlippageBps = details?.currentSlippageBps as number | undefined;
  const recoveryAction = getRecoveryAction(code, morfiError, currentSlippageBps);

  const userMessageKey =
    (morfiError !== undefined ? lookupErrorMessageKey(morfiError) : undefined) ??
    TX_ERROR_MESSAGES[code];

  const params: {
    code: TxErrorCodeType;
    stage: TxStage;
    traceId: string;
    recoveryAction: RecoveryAction;
    userMessageKey: string;
    details?: Record<string, unknown> | undefined;
    morfiError?: string | undefined;
  } = {
    code,
    stage,
    traceId,
    recoveryAction,
    userMessageKey,
  };

  if (details !== undefined) {
    params.details = details;
  }

  if (morfiError !== undefined) {
    params.morfiError = morfiError;
  }

  return new TxError(params);
}
